import { useEffect } from 'react';
import site from '../content/site';

/**
 * Keeps document metadata in sync during client-side navigation.
 *
 * Crawlers get correct meta from the static per-route HTML that
 * scripts/generate-seo.js writes at build time — this hook exists so the
 * title, canonical and social tags stay correct for users and analytics
 * once React takes over routing.
 *
 * Done imperatively rather than by rendering <title>/<meta> so we replace the
 * tags already in the document instead of appending duplicates.
 */
function setMeta(selector, attr, key, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

export default function useSeo({ path, title, description, schema, image, ogImage }) {
  useEffect(() => {
    const url = `${site.url}${path}`;
    const img = `${site.url}${image || ogImage || '/og-image.jpg'}`;

    document.title = title;
    setMeta('meta[name="description"]', 'name', 'description', description);
    setMeta('meta[property="og:title"]', 'property', 'og:title', title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setMeta('meta[property="og:url"]', 'property', 'og:url', url);
    setMeta('meta[property="og:image"]', 'property', 'og:image', img);
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', img);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // Replace the build-time JSON-LD with this route's graph.
    const prev = document.head.querySelector('script[type="application/ld+json"][data-route]');
    if (prev) prev.remove();
    if (schema) {
      const tag = document.createElement('script');
      tag.type = 'application/ld+json';
      tag.setAttribute('data-route', path);
      tag.textContent = JSON.stringify(schema);
      document.head.appendChild(tag);
    }
  }, [path, title, description, schema, image, ogImage]);
}
